import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Toast } from 'primereact/toast';

import fetchApi, { fetchDownload } from '../lib/fetchApi.js';

const Quotation = ({ setPageParams }) => {
  const toast = useRef(null);
  const navigate = useNavigate();

  const [quotations, setQuotations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ customer_name: '', company_name: '' });
  const [loading, setLoading] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);

  useEffect(() => {
    setPageParams({ alias: 'quotation', title: '報價記錄' });
    refresh();
  }, []);

  const refresh = async (nextPage = page) => {
    setLoading(true);
    const response = await fetchApi('/api/v1/quotation/getQuotations', {
      page: nextPage,
      pageSize,
      ...filters,
    });
    if (response?.state === 1) {
      setQuotations(response.data?.quotations || []);
      setTotal(response.data?.total || 0);
      setPage(nextPage);
    } else {
      toast.current?.show({ severity: 'error', summary: '報價記錄', detail: '讀取失敗' });
    }
    setLoading(false);
  };

  const onTableAreaClick = (e) => {
    // 詳見 DIRECTIONS.md §0 的 DataTable 詞條：點非資料列 → 清除選取
    if (!e.target.closest('tbody tr')) {
      setSelectedQuotation(null);
    }
  };

  const downloadPdf = async (id) => {
    // window.open() bypasses fetchApi and strips the Likkan-API-KEY
    // header → backend rejects with invalid_api_key. Use fetch+Blob
    // so the header + session cookie are both sent.
    const result = await fetchDownload(
      `/api/v1/quotation/downloadQuotation?id=${id}`,
      `quotation_${id.slice(0, 8)}.pdf`,
    );
    if (!result.ok) {
      toast.current?.show({ severity: 'error', summary: '下載 PDF', detail: result.error || '失敗' });
    }
  };

  const formatDate = (s) => (s ? new Date(s).toLocaleString('zh-Hant') : '');

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Toolbar
        className="likkan-toolbar mb-3"
        pt={{
          start: { className: 'flex align-items-center gap-2' },
          end:   { className: 'flex align-items-center gap-2' },
        }}
        start={
          <div className="likkan-toolbar-filters">
            <span>客戶姓名：</span>
            <InputText
              value={filters.customer_name}
              onChange={(e) => setFilters((f) => ({ ...f, customer_name: e.target.value }))}
              placeholder="模糊搜尋"
            />
            <span>公司名稱：</span>
            <InputText
              value={filters.company_name}
              onChange={(e) => setFilters((f) => ({ ...f, company_name: e.target.value }))}
              placeholder="模糊搜尋"
            />
            <Button label="搜尋" icon="pi pi-search" size="small" onClick={() => refresh(1)} />
          </div>
        }
        end={
          <Button
            label="新增報價"
            icon="pi pi-plus"
            size="small"
            onClick={() => navigate('/orderform')}
          />
        }
      />

      <div onClick={onTableAreaClick}>
        <DataTable
          value={quotations}
          loading={loading}
          stripedRows
          emptyMessage="未有記錄"
          paginator
          rows={pageSize}
          totalRecords={total}
          first={(page - 1) * pageSize}
          onPage={(e) => refresh(e.page + 1)}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onRowsPerPageChange={(e) => { setPageSize(e.value); refresh(1); }}
          dataKey="id"
          selectionMode="single"
          selection={selectedQuotation}
          onRowClick={(e) => navigate(`/quotation/${e.data.id}`)}
          onSelectionChange={(e) => {
            // 詳見 DIRECTIONS.md §0 的 DataTable 詞條：點同一列保持選取。
            if (e.value !== null && e.value !== undefined) setSelectedQuotation(e.value);
          }}
        >
        <Column field="created_at" header="建立日期" body={(r) => formatDate(r.created_at)} style={{ width: 170 }} />
        <Column field="company_name" header="公司" />
        <Column field="customer_name" header="客戶姓名" />
        <Column field="contact_phone" header="聯絡電話" style={{ width: 130 }} />
        <Column field="moving_date" header="搬運日期" style={{ width: 110 }} />
        <Column field="moving_amount" header="搬運費" body={(r) => `$${Number(r.moving_amount || 0).toFixed(2)}`} style={{ width: 110 }} />
        <Column
          header="操作"
          style={{ width: 220 }}
          body={(r) => (
            <div className="likkan-row-actions-inline">
              <Button
                label="編輯"
                icon="pi pi-pencil"
                size="small"
                onClick={(e) => { e.stopPropagation(); navigate(`/quotation/${r.id}`); }}
              />
              <Button
                label="PDF"
                icon="pi pi-file-pdf"
                severity="success"
                size="small"
                onClick={(e) => { e.stopPropagation(); downloadPdf(r.id); }}
              />
            </div>
          )}
        />
      </DataTable>
      </div>
    </>
  );
};

export default Quotation;
